import { useState } from "react";
import { CalendarRange, Tag } from "lucide-react";
import type { ItineraryItemType } from "@/lib/types";
import UnifiedItinerary, {
  type UnifiedAddStopRequest,
} from "./UnifiedItinerary";
import DayItinerary from "./DayItinerary";
import type { StewardSeed } from "../steward/StewardWorkspace";
import type { StewardGap } from "@/lib/types";

export type TripPlanView = "itinerary" | "fares";

interface TripPlanViewsProps {
  tripId: string;
  /** "fares" when an email deep-link asked for a leg or the fare experience. */
  initialView?: TripPlanView;
  addStopRequest?: UnifiedAddStopRequest | null;
  onAddStopConsumed?: () => void;
  onAskSteward?: (seed?: StewardSeed) => void;
  stewardGap?: StewardGap | null;
}

const TABS: Array<{ id: TripPlanView; label: string; icon: typeof CalendarRange }> = [
  { id: "itinerary", label: "Itinerary", icon: CalendarRange },
  { id: "fares", label: "Fares", icon: Tag },
];

/**
 * The trip page's two ways of looking at the same trip.
 *
 * **Itinerary** is the default and the document: day by day, collapsible,
 * printable. **Fares** is the existing leg-first experience moved here whole —
 * price history, matrices, alternates. That detail is genuinely useful while you
 * are shopping for a fare, and useless once the trip is the thing you are
 * carrying; the segmented control is the line between those two jobs.
 *
 * `UnifiedItinerary` is unchanged. It is mounted, not rewritten.
 */
export default function TripPlanViews({
  tripId,
  initialView = "itinerary",
  addStopRequest,
  onAddStopConsumed,
  onAskSteward,
  stewardGap,
}: TripPlanViewsProps) {
  const [view, setView] = useState<TripPlanView>(initialView);

  return (
    <div className="space-y-3">
      <div
        role="tablist"
        aria-label="Trip view"
        className="inline-flex w-full rounded-lg border border-border bg-muted/50 p-1 sm:w-auto print:hidden"
      >
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = view === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              aria-controls={"trip-view-" + tab.id}
              onClick={() => setView(tab.id)}
              className={
                "inline-flex min-h-[36px] flex-1 items-center justify-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-semibold transition-colors motion-reduce:transition-none sm:flex-none " +
                (active
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground")
              }
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Both panels stay mounted-on-demand rather than always: the fares view
          fires its own queries and we do not want that work on a page whose
          reader only wanted to read their trip. */}
      {view === "itinerary" ? (
        <div id="trip-view-itinerary" role="tabpanel">
          <DayItinerary
            tripId={tripId}
            addStopRequest={addStopRequest as { date?: string; type?: ItineraryItemType } | null}
            onAddStopConsumed={onAddStopConsumed}
          />
        </div>
      ) : (
        <div id="trip-view-fares" role="tabpanel">
          <UnifiedItinerary
            tripId={tripId}
            addStopRequest={addStopRequest}
            onAddStopConsumed={onAddStopConsumed}
            onAskSteward={onAskSteward}
            stewardGap={stewardGap}
          />
        </div>
      )}
    </div>
  );
}
